/**
 * Hook per lo storico email di un cliente (thread Gmail/Outlook dedup +
 * riassunto IA), consumato dalla scheda cliente.
 *
 * Wrappa `src/lib/services/clientMailService.ts` (Task 9): fetch al
 * mount/cambio `clientId`, auto-generazione riassunto una sola volta per
 * cliente quando ci sono thread ma nessun riassunto, paginazione cursor-based
 * con `loadMore`. Tutti gli errori sono soft (stato `error`, mai throw).
 *
 * Guardie anti-race (tre ref complementari):
 * - `requestIdRef`: incrementato ad ogni `load()`; una risposta di
 *   load/loadMore risolta dopo un load piu' recente viene scartata (non
 *   sovrascrive dati piu' freschi).
 * - `clientIdRef`: clientId corrente; loadMore/regenerateSummary lo usano per
 *   resettare i flag `loadingMore`/`summaryGenerating` nel finally SOLO se il
 *   cliente non e' cambiato (un refresh same-client bumpa requestIdRef ma non
 *   deve lasciare i flag bloccati) e per scartare i loro dati cross-client.
 * - `loadedForRef`: clientId a cui appartengono i dati in stato; l'effect di
 *   auto-generazione parte solo se `loadedForRef.current === clientId`, cosi'
 *   al cambio cliente non valuta threads/summary stale del cliente precedente
 *   (i setState di reset non hanno ancora ri-renderizzato).
 *
 * Design doc: MD/features/2026-07-24-email-storico-scheda-cliente-design.md
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  fetchClientMailThreads,
  regenerateClientMailSummary,
  type ClientMailThread,
  type ClientMailStats,
  type ClientMailSummary,
} from '../services/clientMailService';

export function useClientEmailThreads(clientId: string | null) {
  const [threads, setThreads] = useState<ClientMailThread[]>([]);
  const [stats, setStats] = useState<ClientMailStats | null>(null);
  const [summary, setSummary] = useState<ClientMailSummary | null>(null);
  const [newEmailCount, setNewEmailCount] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [summaryGenerating, setSummaryGenerating] = useState(false);
  const [error, setError] = useState(false);
  const autoGenTried = useRef<string | null>(null);
  const requestIdRef = useRef(0);
  const clientIdRef = useRef<string | null>(clientId);
  const loadedForRef = useRef<string | null>(null);

  const load = useCallback(async () => {
    if (!clientId) return;
    const cid = clientId;
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(false);
    try {
      const resp = await fetchClientMailThreads(cid);
      if (requestIdRef.current !== requestId) return; // stale: un load piu' recente ha preso il posto
      setThreads(resp.threads);
      setStats(resp.stats);
      setSummary(resp.summary);
      setNewEmailCount(resp.new_email_count);
      setNextCursor(resp.next_cursor);
      loadedForRef.current = cid; // i dati in stato appartengono ora a questo cliente
    } catch {
      if (requestIdRef.current === requestId) setError(true);
    } finally {
      if (requestIdRef.current === requestId) setLoading(false);
    }
  }, [clientId]);

  // Reset al mount/cambio clientId. `load` cambia identita' solo al cambio di
  // clientId, quindi questo effect rifira esattamente in quel caso. Dichiarato
  // PRIMA dell'effect di auto-generazione: nello stesso commit azzera
  // loadedForRef, cosi' l'auto-gen non parte sui dati stale del cliente precedente.
  useEffect(() => {
    // Bump anche qui (non solo in load): copre la transizione a clientId=null,
    // dove load() early-returna e una risposta in volo del cliente precedente
    // atterrerebbe in stato senza cliente selezionato. Al mount/cambio con
    // clientId valido il load() successivo bumpa di nuovo e supera il proprio guard.
    requestIdRef.current += 1;
    clientIdRef.current = clientId;
    loadedForRef.current = null;
    setThreads([]);
    setStats(null);
    setSummary(null);
    setNewEmailCount(0);
    setNextCursor(null);
    setLoadingMore(false);
    setSummaryGenerating(false);
    void load();
  }, [clientId, load]);

  const regenerateSummary = useCallback(async () => {
    if (!clientId || summaryGenerating) return;
    const cid = clientId;
    setSummaryGenerating(true);
    try {
      const s = await regenerateClientMailSummary(cid);
      if (clientIdRef.current === cid && s) {
        setSummary(s);
        setNewEmailCount(0);
      }
    } catch {
      /* soft: il riassunto resta quello precedente */
    } finally {
      // Guard su clientId (NON su requestId): un refresh same-client non deve
      // lasciare summaryGenerating bloccato a true.
      if (clientIdRef.current === cid) setSummaryGenerating(false);
    }
  }, [clientId, summaryGenerating]);

  // Ref "latest callback": evita che l'identita' di regenerateSummary (che
  // cambia con summaryGenerating) entri nelle deps dell'effect di auto-gen
  // facendolo rifirare inutilmente.
  const regenerateSummaryRef = useRef(regenerateSummary);
  useEffect(() => {
    regenerateSummaryRef.current = regenerateSummary;
  });

  // Prima apertura: genera il riassunto una sola volta per cliente se ci sono
  // thread ma nessun riassunto. `loadedForRef.current === clientId` garantisce
  // che threads/summary valutati appartengano davvero a QUESTO cliente e non
  // siano lo stato stale del precedente (setState di reset non ancora applicati);
  // evita anche di bruciare autoGenTried per il nuovo cliente sui dati altrui.
  useEffect(() => {
    if (!clientId || loading || summary || threads.length === 0) return;
    if (loadedForRef.current !== clientId) return;
    if (autoGenTried.current === clientId) return;
    autoGenTried.current = clientId;
    void regenerateSummaryRef.current();
  }, [clientId, loading, summary, threads.length]);

  const loadMore = useCallback(async () => {
    if (!clientId || !nextCursor || loadingMore) return;
    const cid = clientId;
    const requestId = requestIdRef.current;
    setLoadingMore(true);
    try {
      const resp = await fetchClientMailThreads(cid, nextCursor);
      // Dati: guard su requestId — copre sia il cross-client (il cambio cliente
      // passa sempre da load() che bumpa il contatore) sia il refresh same-client
      // (che ha ricaricato pagina 1: appendervi la vecchia pagina 2 duplicherebbe).
      if (requestIdRef.current === requestId) {
        setThreads((prev) => [...prev, ...resp.threads]);
        setNextCursor(resp.next_cursor);
      }
    } catch {
      /* soft */
    } finally {
      // Flag: guard su clientId — dopo un refresh same-client il flag va
      // comunque resettato, altrimenti "Carica altri" resta bloccato.
      if (clientIdRef.current === cid) setLoadingMore(false);
    }
  }, [clientId, nextCursor, loadingMore]);

  return {
    threads,
    stats,
    summary,
    newEmailCount,
    loading,
    error,
    hasMore: nextCursor !== null,
    loadingMore,
    loadMore,
    refresh: load,
    regenerateSummary,
    summaryGenerating,
  };
}
